import Link from 'next/link';
import { FC, useEffect } from 'react';

import { Anchor, Badge, Box, Button, Group, Pagination, Space, Stack, Text } from '@mantine/core';

import { CompanyHelpers } from 'helpers/CompanyHelpers';

import { Controller } from 'components/banners/Controller';
import { ProductSeries } from 'components/company-profile/sections/ProductSeries';
import { ComponentOverviewHits } from 'components/component-overview/ComponentOverviewHits';
import { ComponentSearchForm } from 'components/component-overview/ComponentSearchForm';
import { AddProductCard } from 'components/component-overview/components/AddProductCard';
import { FilterSuggestions } from 'components/component-overview/components/FilterSuggestions';
import { NoExactProductMatches } from 'components/component-overview/components/NoExactProductMatches';
import { SearchBar } from 'components/component-overview/components/SearchBar';
import { useComponentSearchData } from 'components/component-overview/hooks/use-component-search-data';
import { useComponentSearchFilters } from 'components/component-overview/hooks/use-component-search-filters';
import { useSetFiltersFromQuery } from 'components/component-overview/hooks/use-set-filters-from-query';
import { InlineFilters } from 'components/component-overview/inline-filters/InlineFilters';
import { SearchService } from 'components/component-overview/services/SearchService';
import { ErrorBoundary } from 'components/error-boundary/ErrorBoundary';
import { Page } from 'components/page/Page';
import { useCompanyProfile } from 'hooks/use-company-profile';
import { useHeroMedia } from 'hooks/use-hero-media';
import { useLocalEvent } from 'hooks/use-local-event';
import { FeedbackService } from 'services/FeedbackService';
import { InternalTrackingService } from 'services/InternalTrackingService';

const ComponentOverview: FC = () => {
    const { localEvent } = useLocalEvent();
    const { image: heroImage } = useHeroMedia();

    const { manufacturer, type, search } = useComponentSearchFilters();
    const { docs: products, page, totalPages, isLoading, suggestions, makers } = useComponentSearchData();
    const [namedMaker] = makers ?? [];

    const searchesControllers =
        type === 'controller' || Boolean(suggestions?.some(({ filter }) => filter.type === 'controller'));

    const { company } = useCompanyProfile((manufacturer as string) || null);

    // search by URL query params
    useSetFiltersFromQuery();

    useEffect(() => {
        SearchService.setFilter('event', localEvent?.id);
        SearchService.debouncedSearch();
    }, [localEvent?.id]);

    useEffect(() => {
        return () => {
            SearchService.setFilter('event', undefined);
        };
    }, []);

    const showPagination = !isLoading && totalPages > 1;

    const promoCard = page === 0 ? <AddProductCard /> : undefined;

    return (
        <ErrorBoundary onError={() => InternalTrackingService.track('error.boundary.products')}>
            <Page showBackground showScrollToTop title="Find Products">
                <Page.Hero image={heroImage} showReplusLogo>
                    <Stack gap="xl" maw={600} mx="auto">
                        <Page.Title ta="center">Find Products</Page.Title>
                        <SearchBar showSubmit />

                        <Group gap="xs" justify="center" opacity={0.6}>
                            <Badge variant="outline" color="white" size="xs">
                                BETA
                            </Badge>
                            <Text inherit c="white">
                                Search, products and datasheets are evolving.{' '}
                                <Anchor c="inherit" type="button" onClick={() => FeedbackService.open()} td="underline">
                                    Feedback
                                </Anchor>{' '}
                                is encouraged!
                            </Text>
                        </Group>
                    </Stack>
                </Page.Hero>

                <Page.WideContent id="search-content" pt="lg">
                    <Stack gap="xs">
                        <ComponentSearchForm>
                            <InlineFilters variant="light" hideFilters={['portPower']} />
                        </ComponentSearchForm>
                        <FilterSuggestions />
                    </Stack>
                    {searchesControllers && <Controller />}
                    <NoExactProductMatches />
                    <ComponentOverviewHits
                        hits={products}
                        isLoading={isLoading}
                        promoCard={promoCard}
                        emptyMessage={
                            namedMaker && (
                                <Text inherit>
                                    No matching products from {namedMaker.name}.{' '}
                                    <Anchor
                                        component={Link}
                                        href={CompanyHelpers.urls.view(namedMaker.slug)}
                                        onClick={() =>
                                            InternalTrackingService.track('product.search.maker.click', {
                                                query: search,
                                                company: namedMaker.slug,
                                            })
                                        }
                                        inherit
                                        fw={600}
                                    >
                                        Visit their profile
                                    </Anchor>
                                </Text>
                            )
                        }
                    />

                    <Pagination
                        value={page + 1}
                        onChange={(newPage) => {
                            SearchService.setPage(newPage - 1);
                            SearchService.scrollToContent();
                        }}
                        total={totalPages}
                        hidden={!showPagination}
                        disabled={isLoading}
                    />
                </Page.WideContent>

                {!isLoading && !products.length && company?.productSeries.length && (
                    <Box bg="gray.1">
                        <Page.WideContent>
                            <Space h="xl" />
                            <ProductSeries
                                company={company}
                                title={`Product Series by ${company.name}`}
                                smallTitle={true}
                                filterable={false}
                            />
                            <Space h="md" />
                            <Group justify="center">
                                <Button
                                    component={Link}
                                    href={CompanyHelpers.urls.view(company.slug)}
                                    size="lg"
                                    radius="xl"
                                >
                                    Visit profile
                                </Button>
                            </Group>
                            <Space h="xl" />
                        </Page.WideContent>
                    </Box>
                )}
            </Page>
        </ErrorBoundary>
    );
};

export { ComponentOverview };
